import React from "react"
import { notification } from "antd"
import api from "src/api"

/**
 * Галерея точки. Каждое действие уходит на бэк сразу, отдельным запросом:
 * файл кладётся в S3, а не в форму, и откатывать его по «Отмене» карточки нечем.
 * Ответ всегда содержит актуальный список фото — его и держим в состоянии.
 */
export function usePointPhotos(pointId) {
  const [photos, setPhotos] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(false)

  // Единственный источник списка — запрос за галереей при открытии карточки.
  // Фото из списка точек здесь не берём: список после удаления не
  // перезапрашивается, и на переоткрытии карточки успевало мелькнуть удалённое
  // изображение, пока не приходил ответ.
  React.useEffect(() => {
    let isActual = true
    setPhotos([])

    if (!pointId) {
      return undefined
    }

    setIsLoading(true)
    api
      .get(`points/${pointId}/photos`)
      .then(({ data }) => {
        if (isActual) {
          setPhotos(data?.items ?? [])
        }
      })
      .catch(() => {
        notification.error({ message: "Ошибка загрузки изображений точки" })
      })
      .finally(() => {
        if (isActual) {
          setIsLoading(false)
        }
      })

    return () => {
      isActual = false
    }
  }, [pointId])

  async function request(sendRequest, errorMessage, successMessage) {
    setIsLoading(true)
    try {
      const { data } = await sendRequest()

      setPhotos(data?.items ?? [])
      notification.success({ message: successMessage })

      return true
    } catch (error) {
      notification.error({
        message: error?.response?.data?.message ?? errorMessage,
      })

      return false
    } finally {
      setIsLoading(false)
    }
  }

  function upload(file) {
    const formData = new FormData()
    formData.append("file", file)

    return request(
      () => api.post(`points/${pointId}/photos`, formData),
      "Ошибка загрузки изображения",
      "Изображение загружено"
    )
  }

  function replace(photoId, file) {
    const formData = new FormData()
    formData.append("file", file)

    return request(
      () => api.post(`points/${pointId}/photos/${photoId}`, formData),
      "Ошибка замены изображения",
      "Изображение заменено"
    )
  }

  function remove(photoId) {
    return request(
      () => api.delete(`points/${pointId}/photos/${photoId}`),
      "Ошибка удаления изображения",
      "Изображение удалено"
    )
  }

  function setMain(photoId) {
    const formData = new FormData()
    formData.append("isMain", 1)

    return request(
      () => api.post(`points/${pointId}/photos/${photoId}`, formData),
      "Ошибка изменения основного изображения",
      "Основное изображение изменено"
    )
  }

  return { photos, isLoading, upload, replace, remove, setMain }
}
